/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Dropdown from '../components/menus/Dropdown';
import {onHandleChange} from '../editors/ChooserUtils';

// defines the prop definitions for language option ...
export interface LanguageOption {
  label: string;
  value: string;
  disabled?: boolean;
};

// defines the prop definitions fro language chooser ...
export interface LanguageChooserProps {
  onChange: (val: string) => void;
  fieldLabel?: string;
  defaultValue?: LanguageOption['value'];
  languageOptions?: LanguageOption[];
  useForm?: boolean;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  includeDefault?: boolean;
  placeholder?: string;
  useGroupDefault?: boolean;
};

// defines the styles used by language chooser ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * Generates the language options list ...
 */
function getLanguageOptions(includeDefault: boolean, useGroupDefault: boolean) {
  const languageOptions: LanguageOption[] = [];
  const languageMap = translate('common:languages', {returnObjects: true});
  !includeDefault && (delete languageMap?.default);
  Object.keys(languageMap)?.forEach((key: string) => {
    if (key === 'default') {
      const label = useGroupDefault ? translate('common:editors.preferences.language.groupDefault') : languageMap[key];
      languageOptions?.push({label, value: key});
    } else {
      languageOptions?.push({label: languageMap[key], value: key});
    }
  });
  return languageOptions;
};

// Returns the default value
const getDefaultValue = (options: LanguageOption[], defaultValue: string, includeDefault: boolean) => {
  const matchIndex = options?.findIndex(({value}: {value: string}) => value === defaultValue);
  return options?.[matchIndex]?.label || (includeDefault ? 'default' : null);
};

/**
 * Allows user to choose a preferred language from one of the available/supported
 * languages.
 *
 * @param onChange: Callback fired with the selected value when a
 *   selection is made
 * @param fieldLabel: field label displayed above input
 * @param hasError: f the field has an error the value is passed on
 * to the dropdown component
 * @param useForm: if true, the children of language chooser component is
 * wrapped inside the form item
 * @param required: if ture, the field is mandatory with an asterisk sign
 * displayed next to the field label
 * @param defaultValue: Default value passed to the component.
 * Will only be considered if passed as LanguageOption.
 * @param readOnly: If true then dropdown is disabled
 *
 * ```typescript
 * import {LanguageChooser} from '@ion/dls/src/editors/';
 *
 * <LanguageChooser options={options}
 *   fieldLabel={translate('common:languages.fieldLabel')}
 *   defaultValue='default'
 *   hasError={true}
 *   includeDefault={false}
 *   readOnly={false}
 *   onChange={handleChange} />
 *
 * ```
 */
export const LanguageChooser = ({
  onChange,
  defaultValue,
  useForm = false,
  required = false,
  hasError = false,
  readOnly = false,
  includeDefault = true,
  useGroupDefault = false,
  fieldLabel = translate('common:editors.preferences.language.title'),
  placeholder = translate('common:editors.preferences.language.placeholder'),
  languageOptions = null
}: LanguageChooserProps) => {
  const classes                   = useStyles();
  const options: LanguageOption[] = languageOptions || getLanguageOptions(includeDefault, useGroupDefault);
  const [selected, setSelected]   = useState<string>(getDefaultValue(options, defaultValue, includeDefault));

  useEffect(() => {
    const matchingOption = getDefaultValue(options, defaultValue, includeDefault);
    if (defaultValue) {
      setSelected(matchingOption);
    }
  }, [defaultValue]);

  if (useForm) {
    return (
      <div className={classes.root}>
        <Dropdown options={options}
          placeholder={placeholder}
          fieldLabel={fieldLabel}
          hasError={hasError}
          disabled={readOnly}
          value={selected}
          labelAsValue={false}
          onChange={onHandleChange({setSelected, onChange, options}, null)} />
      </div>
    );
  }
  return null;
};

export default LanguageChooser;
